import React, { FC, Suspense, useState } from 'react';
import {
  Box,
  Text,
  Separator,
  useSideSheetState,
  SpinnerSize,
  Spinner,
  ToastAction,
  ToastType,
  useToastsDispatch,
} from '@bechtel/gds';
import { AvatarRegularStyled, CloseRequestButtonStyled } from './styled';
import { EllipsisPlacement, PersonaMessages, TextWithEllipsis, useName, ErrorBoundary } from '@bechtel/client-common';
import { useLingui } from '@lingui/react';
import { RequestedByProps } from './types';
import { Trans, t } from '@lingui/macro';
import { SectionToggle } from '../section-toggle';
import { map, noop, orderBy, filter } from 'lodash';
import { WeldAvatarGroup, useRodRoomPermissions } from '@bechtel/client-weld-common';
import { SingleCloseRequest } from '~/request/close/single';
import { CommodityAssignee, PersonaType, RodRequestStatusCode } from '@bechtel/types';

export const RequestedBy: FC<RequestedByProps> = ({ rodRequestItem, onClose }) => {
  const [isRequestedByOpen, setIsRequestedByOpen] = useState(true);
  const { getFullName } = useName();
  const { i18n } = useLingui();
  const { visible, open, close } = useSideSheetState(false);
  const dispatchToast = useToastsDispatch();

  const { canCloseRodRequest } = useRodRoomPermissions();
  const CloseRodRequestToastContent = () => {
    return (
      <Box
        pb={{ sm: '550' }}
        mb={{ sm: '400' }}
        css={`
          border-bottom: 1px solid ${({ theme }) => theme.colors.grey[100]};
        `}
      >
        <Text family="graphik" weight="400" size="500">
          {`${rodRequestItem.requestNumber} ${t`Request has been successfully closed`}`}
        </Text>
      </Box>
    );
  };

  const openCloseRodRequestToast = () => {
    const createCloseRequestToast: ToastAction = {
      type: 'ADD_TOAST',
      payload: {
        toastType: ToastType.INFO,
        content: CloseRodRequestToastContent,
        autoDismissTimeout: 7000,
        alertAction: {
          label: t`Got It`,
          onClickHandler: () => noop,
        },
      },
    };
    dispatchToast(createCloseRequestToast);
  };

  const closeSidesheet = (openToast: boolean) => {
    close();
    if (openToast && onClose) {
      onClose();
      openCloseRodRequestToast();
    }
  };
  const weldOwners = filter(rodRequestItem.welds[0]?.commodityAssignees, (item: CommodityAssignee) => {
    return item.personaType === PersonaType.FOREMAN || item.personaType === PersonaType.FIELD_WELD_ENGINEER;
  });
  return (
    <>
      <SectionToggle
        toggle={() => setIsRequestedByOpen(!isRequestedByOpen)}
        heading={'Requested By'}
        isOpen={isRequestedByOpen}
      />
      {isRequestedByOpen && (
        <Box
          p={{ sm: '500' }}
          css={{ background: 'white' }}
          flexDirection={{ sm: 'column' }}
          flexGrow={{ sm: 1 }}
          justifyContent={{ sm: 'space-between' }}
        >
          <Box flexDirection={{ sm: 'column' }}>
            <Box mb={{ sm: '550' }}>
              <AvatarRegularStyled
                data-testid="requestor-avatar"
                src={rodRequestItem?.createdBy?.imageUrl ? rodRequestItem?.createdBy?.imageUrl : undefined}
              />
              <Box ml={{ sm: '300' }} pb={{ sm: '100' }} css={{ lineHeight: 1.3 }} flexDirection={{ sm: 'column' }}>
                <TextWithEllipsis
                  size="400"
                  weight="600"
                  ellipsisPlacement={EllipsisPlacement.END}
                  data-testid="rod-request-owner-name"
                >
                  {getFullName(rodRequestItem?.createdBy?.firstName, rodRequestItem?.createdBy?.lastName)}
                </TextWithEllipsis>
                <Text size="300" data-testid="rod-request-persona">
                  {i18n._(PersonaMessages[rodRequestItem?.createdBy?.persona])} &nbsp;
                  {rodRequestItem?.createdBy?.crew?.crewNumber}
                </Text>
              </Box>
            </Box>

            {!!rodRequestItem?.comments.length && (
              <Box flexDirection={{ sm: 'column' }} css={{ lineHeight: 1.4 }} mb={{ sm: '500' }}>
                <Box mb={{ sm: '300' }}>
                  <Text size="200" family="graphik" style={{ opacity: 0.6 }}>
                    <Trans>Comment from FM</Trans>
                  </Text>
                </Box>
                <Text size="300" family="graphik" data-testid="rod-request-comment" style={{ wordBreak: 'break-word' }}>
                  {orderBy(rodRequestItem.comments, 'createdAt', 'desc')[0].comment}
                </Text>
              </Box>
            )}
            {weldOwners.length > 0 && (
              <>
                <Separator spacing="000" color="grey.200" />
                <Box mt={{ sm: '500' }} mb={{ sm: '600' }} flexDirection={{ sm: 'column' }}>
                  <Box mb={{ sm: '300' }}>
                    <Text size="200" family="graphik" style={{ opacity: 0.6 }}>
                      <Trans>Ownership</Trans>
                    </Text>
                  </Box>

                  <Box
                    alignItems={{ sm: 'center' }}
                    mt={{ sm: '400' }}
                    css={{ textAlign: 'center' }}
                    data-testid="ownership-container"
                  >
                    {/* TODO: Change below logic for showing Ownership */}
                    {map(weldOwners, (item) => (
                      <Box
                        flexDirection={{ sm: 'column' }}
                        alignItems={{ sm: 'center' }}
                        css={{ position: 'relative' }}
                      >
                        <WeldAvatarGroup weldAssignees={[item]} />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </>
            )}
          </Box>
          {canCloseRodRequest && rodRequestItem.currentStatusI18n !== RodRequestStatusCode.CLOSED && (
            <CloseRequestButtonStyled
              onClick={() => {
                open();
              }}
            >
              <Trans> Close Request</Trans>
            </CloseRequestButtonStyled>
          )}
          {visible && (
            <ErrorBoundary>
              <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
                <SingleCloseRequest rodRequest={rodRequestItem} onClose={closeSidesheet} />
              </Suspense>
            </ErrorBoundary>
          )}
        </Box>
      )}
    </>
  );
};
